import React from "react";

export default function Privacy() {
  return (
    <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="privacy-page">
      <div className="overline">Legal</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mt-3 mb-8">
        Privacy Policy
      </h1>
      <p className="text-zinc-400 text-sm mb-10">Last updated: August 2026 · GDPR compliant</p>

      <div className="prose prose-invert max-w-none space-y-6 text-zinc-300 leading-relaxed">
        <section>
          <h2 className="font-serif text-2xl gold mb-3">1. Data Controller</h2>
          <p>
            The controller responsible for your personal data is{" "}
            <b>Chef Jacinto Samuel</b>, operating <b>Jacinto's Culinary Atelier</b>{" "}
            at{" "}
            <a href="https://jacintosculinaryatelier.com" className="gold underline">
              jacintosculinaryatelier.com
            </a>
            . Contact:{" "}
            <a href="mailto:jacintof.samuel@gmail.com" className="gold underline">
              jacintof.samuel@gmail.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">2. What Data We Collect</h2>
          <p>We only collect what is necessary to run the Atelier:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Account data</b> — name and email address (via Google Sign-In)</li>
            <li><b>Purchase data</b> — items purchased, order date, amount (via Stripe)</li>
            <li><b>Gift data</b> — sender and recipient emails when using Gift-a-Pass</li>
            <li><b>Usage data</b> — anonymised traffic statistics (via Google Analytics)</li>
            <li><b>Support data</b> — content of messages you send us</li>
          </ul>
          <p>We do <b>not</b> collect or store your payment card details. Stripe handles that securely.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">3. Legal Basis (GDPR Article 6)</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Contract</b> — to deliver the recipes you purchase</li>
            <li><b>Legitimate interest</b> — to keep the Atelier safe and improve it</li>
            <li><b>Consent</b> — for analytics cookies and marketing emails</li>
            <li><b>Legal obligation</b> — to keep tax and accounting records</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">4. Who We Share Data With</h2>
          <p>We share the minimum necessary data with these trusted processors:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Stripe</b> — payment processing (USA/EU, PCI-DSS compliant)</li>
            <li><b>Google</b> — authentication &amp; analytics (Google Ireland)</li>
            <li><b>Resend</b> — transactional emails for gifts (EU)</li>
            <li><b>Emergent</b> — hosting infrastructure</li>
            <li><b>Cloudflare</b> — content delivery and security (EU)</li>
          </ul>
          <p>We never sell your data to third parties.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">5. Data Retention</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Account data — until you request deletion</li>
            <li>Purchase records — 7 years (tax law)</li>
            <li>Analytics — 14 months (Google Analytics default)</li>
            <li>Support messages — 2 years</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">6. Your Rights (GDPR)</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data ("right to be forgotten")</li>
            <li>Restrict or object to processing</li>
            <li>Data portability (receive your data in a machine-readable format)</li>
            <li>Withdraw consent at any time (where processing is based on consent)</li>
            <li>Lodge a complaint with your national data protection authority</li>
          </ul>
          <p>
            To exercise any of these rights, email{" "}
            <a href="mailto:jacintof.samuel@gmail.com" className="gold underline">
              jacintof.samuel@gmail.com
            </a>
            . We respond within 30 days.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">7. Cookies</h2>
          <p>
            The Atelier uses a small number of cookies. Essential cookies (login,
            session, payment) are always active. Analytics cookies only run if you accept
            them via the cookie banner. See our{" "}
            <a href="/cookies" className="gold underline">Cookie Notice</a> for details.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">8. International Transfers</h2>
          <p>
            Some of our processors (Stripe, Google) may transfer data outside the EU
            under Standard Contractual Clauses approved by the European Commission,
            providing equivalent protection.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">9. Security</h2>
          <p>
            We use HTTPS across the site, encrypted database connections, and industry-
            standard access controls. However, no system is 100% secure. If we detect a
            breach affecting your data, we will notify you within 72 hours.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">10. Children</h2>
          <p>
            The Atelier is not directed to children under 16. We do not knowingly
            collect data from minors. If you believe we have, contact us and we will
            delete it.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl gold mb-3">11. Changes to This Policy</h2>
          <p>
            We may update this policy occasionally. Material changes will be highlighted
            on the site and, where required, notified by email.
          </p>
        </section>
      </div>
    </main>
  );
}
